import React, { forwardRef, useEffect, useState } from 'react';

type CountdownPart =
    | { type: 'digit'; value: string }
    | { type: 'separator'; value: string };

function formatCountdown(remainingMs: number): CountdownPart[] {
    const clamped = Math.max(0, remainingMs);
    const totalSeconds = Math.floor(clamped / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const groups = [days, hours, minutes, seconds].map((n) => String(n).padStart(2, '0'));

    const parts: CountdownPart[] = [];
    groups.forEach((group, i) => {
        if (i > 0) { parts.push({ type: 'separator', value: ':' }); }
        for (const ch of group) { parts.push({ type: 'digit', value: ch }); }
    });
    return parts;
}

const FLIP_DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

function FlipDigit({ value }: { value: string }) {
    const digit = Number(value);
    return (
        <span className="splash__flip-digit">
            <span
                className="splash__flip-digit-column"
                style={{ transform: `translateY(-${digit}em)` }}
            >
                {FLIP_DIGITS.map((d) => (
                    <span key={d}>{d}</span>
                ))}
            </span>
        </span>
    );
}

function CountdownDisplay({ parts }: { parts: CountdownPart[] }) {
    return (
        <>
            {parts.map((part, i) => (
                part.type === 'digit'
                    ? <FlipDigit key={i} value={part.value} />
                    : <span key={i} className="splash__countdown-separator">{part.value}</span>
            ))}
        </>
    );
}

type CountdownProps = {
    targetMs: number;
};

export const Countdown = forwardRef<HTMLDivElement, CountdownProps>(function Countdown(
    { targetMs },
    ref,
) {
    const [parts, setParts] = useState(() => formatCountdown(targetMs - Date.now()));

    useEffect(() => {
        const tick = () => setParts(formatCountdown(targetMs - Date.now()));
        const id = window.setInterval(tick, 1000);
        return () => window.clearInterval(id);
    }, [targetMs]);

    return (
        <div className="splash__countdown" ref={ref}>
            <CountdownDisplay parts={parts} />
        </div>
    );
});
